import { find } from 'lodash';
import { IntlFormatters } from 'react-intl';
import { getEvent, getEventStreamData, getRace } from '../commands/sportsCommands';
import { PLACEHOLDER_STREAM_URL } from '../data-types/dialogs/dialogConstants';
import { SCREEN_SIZE_FOR_THREE_OUTCOMES, SCREEN_SIZE_FOR_TWO_OUTCOMES } from '../data-types/events/eventsConstants';
import { DropdownSelectedIndexes } from '../data-types/events/eventsInterfaces';
import { GHOST_DIALOG_TYPE } from '../data-types/login/loginEnums';
import { eventStatuses, offerStatuses, sportCategories } from '../data-types/sports/sportsConstants';
import { SPORT_LIST_ID, TEMPLATE_ID } from '../data-types/sports/sportsEnums';
import { BetEventItem, DropdownMarket, EventItem, MarketOutcome, Offer, Ticket, Time } from '../data-types/sports/sportsInterfaces';
import { STREAMING_SUPPLIER } from '../data-types/stream/streamEnums';
import { WatchStreamData } from '../data-types/stream/streamInterfaces';
import { SubscribeEventInfo } from '../data-types/ticket/ticketInterfaces';
import { SportsbookLocation } from '../data-types/url/urlInterfaces';
import { BETRADAR_STREAMING_ENABLED, GREYHOUND_STREAMING_ENABLED, HORSE_STREAMING_ENABLED } from '../initialisation/initialisation';
import { getTranslatedStateHeader } from '../international/translation/sportPeriods';
import { toggleGhostUserDialog } from '../state/actions/dialogActions';
import {
  setStreamBetradarStatus,
  setStreamIframeEventId,
  setStreamIframeSource,
  setStreamingSupplier,
} from '../state/actions/iframeActions';
import { setFadeOutOverlayData } from '../state/actions/navigationActions';
import { SportsbookEventsState } from '../state/types/sportsbookEventsType';
import { posthogTrackStreamOpen } from './posthog';
import { enqueueSnackbarPrompt } from './prompts';
import { liveEventStates } from './sports';
import { getKeysFromEventsMap } from './sportsbook';
import { hasMadeAFirstDeposit, isGhostUser } from './user';

const LIVE_OR_UPCOMING_STATES = [eventStatuses.UPCOMING, eventStatuses.LIVE, eventStatuses.ABOUT_TO_START];

export const isEventLiveOrUpcoming = (event: EventItem): boolean => LIVE_OR_UPCOMING_STATES.some(status => status === event.status);

/**
 * Checks whether and event is in a particular state.
 * @param event The event whose state is to be checked
 * @param status The status we're interested in. The value is one of 'eventStatuses' constant.
 * @returns {boolean} True, if the event is in the specified status, false otherwise.
 */
export const isEventInState = (event: EventItem, status: number): boolean => event.status === status;

/**
 * Merges static and real-time event updates into an event.
 * @param event The original event (containing the static data)
 * @param liveEvent The event containing the latest update
 * @returns {EventItem} The merged event details. The properties on the live events have priority.
 */
export const mergeEventDetails = (event: BetEventItem, liveEvent: EventItem | undefined): BetEventItem => ({
  ...event,
  ...(liveEvent || {}),
});

export const mergeSystemBetEventDetails = (event: EventItem, liveEvent: EventItem | undefined): EventItem => ({
  ...event,
  ...(liveEvent || {}),
});

// was taken over from MatchTime.tsx
export const getMatchTime = (event: EventItem, time: Time, formatMessage: IntlFormatters['formatMessage']): string | null => {
  if (event.details) {
    switch (event.sportId) {
      case sportCategories.BASKETBALL:
      case sportCategories.E_BASKETBALL:
        return `${liveEventStates(event.details.state, event.sportId, formatMessage)} ${
          time.remaining && time.remaining.minutes !== null ? `${time.remaining.minutes}'` : ''
        }`;

      case sportCategories.TENNIS:
        return liveEventStates(event.details.state, event.sportId, formatMessage);
      case sportCategories.FOOTBALL:
        return `${
          time.minutes === 45
            ? formatMessage({ id: 'sports.half_time_short' })
            : time.extra && time.extra.minutes
              ? `${time.minutes}+${time.extra.minutes}'`
              : `${time.minutes}'`
        }`;
      case sportCategories.E_FOOTBALL:
        return `${time.extra && time.extra.minutes ? `${time.minutes}+${time.extra.minutes}'` : `${time.minutes}'`}`;
      case sportCategories.CRICKET:
        return `${event.details.matchType ? event.details.matchType.toUpperCase() : ''}`;
      case sportCategories.DARTS:
        if (!event.bestOf) {
          return '';
        }
        if (event.bestOf.leg) {
          return formatMessage({ id: 'sports.dart_periods_leg' }, { legs: event.bestOf.numOfLeg }).toUpperCase();
        }
        return formatMessage({ id: 'sports.dart_periods_set' }, { sets: event.bestOf.numOfSet }).toUpperCase();

      case sportCategories.SNOOKER:
        if (!event.totalFrame) {
          return '';
        }
        return formatMessage({ id: 'sports.snooker_periods' }, { frames: event.totalFrame });

      case sportCategories.RUGBY:
      case sportCategories.AMERICAN_FOOTBALL:
      case sportCategories.ICE_HOCKEY:
        return `${liveEventStates(event.details.state, event.sportId, formatMessage)} ${time.minutes}'`;
      case sportCategories.GOLF:
        return formatMessage({ id: 'common.live' });
      default:
        return null;
    }
  }

  return null;
};

export const getTime = (event: EventItem, fallbackMatchTime: string, formatMessage: IntlFormatters['formatMessage'], liveText?: string) => {
  switch (event.templateId) {
    case TEMPLATE_ID.ONE:
      const translatedStateHeader = event.stateHeader ? getTranslatedStateHeader(event.stateHeader, event.sportId, formatMessage) : '';

      return (liveText && translatedStateHeader ? `${liveText} - ${translatedStateHeader}` : translatedStateHeader) || fallbackMatchTime;
    case TEMPLATE_ID.TWO:
      return (event.stateHeader && event.stateHeader.toUpperCase()) || fallbackMatchTime.toUpperCase();
    default:
      return fallbackMatchTime;
  }
};

export const getLiveMatchTime = (event: EventItem, formatMessage: IntlFormatters['formatMessage']): string => {
  const matchTime = getMatchTime(event, event.details ? event.details.time : { minutes: 0, seconds: 0 }, formatMessage);
  const liveText = formatMessage({ id: 'common.live' }).toUpperCase();
  const fallbackText = matchTime ? `${liveText} - ${getTime(event, matchTime, formatMessage)}` : liveText;

  return getTime(event, fallbackText, formatMessage, liveText);
};

export const mergeUniqueEvents = (eventsList1: EventItem[], eventsList2: EventItem[]) => {
  const sanitizedEvents = [] as EventItem[];

  eventsList1.forEach(event => {
    const foundEvent = find(sanitizedEvents, { id: event.id });
    if (!foundEvent) {
      sanitizedEvents.push(event);
    }
  });

  eventsList2.forEach(event => {
    const foundEvent = find(sanitizedEvents, { id: event.id });
    if (!foundEvent) {
      sanitizedEvents.push(event);
    }
  });

  return sanitizedEvents;
};

export const getEventStreamInfo = (event: EventItem | undefined) => {
  const streamSource = (event && event.streams && event.streams.length > 0 && event.streams[0].url) || '';
  const streamAvailable = event && event.streamAvailable;

  return { streamSource, canDisplay: !!(streamSource || streamAvailable) };
};

export const closeStreamIframe = () => {
  setFadeOutOverlayData({ open: false, zIndex: 0, onClick: undefined });
  setStreamIframeSource(undefined);
  setStreamIframeEventId(undefined);
  setStreamingSupplier(undefined);
  setStreamBetradarStatus(undefined);
};

const handleBetradarStreamingSetup = async (eventId: number) => {
  try {
    const betradarStreamData = await getEventStreamData(eventId);
    if (betradarStreamData) {
      setStreamBetradarStatus(betradarStreamData.betradarStatus);
      if (betradarStreamData.streamUrl) {
        setStreamIframeSource(betradarStreamData.streamUrl);
      } else {
        setStreamIframeSource(PLACEHOLDER_STREAM_URL);
      }
      return true;
    }
  } catch (error) {
    closeStreamIframe();
    enqueueSnackbarPrompt(error.message, { closeOnClick: true });
  }

  return false;
};

export const watchStream = (data: WatchStreamData) => async () => {
  const { eventId, stream, supplier, trackData } = data;
  const isBetradar = supplier === STREAMING_SUPPLIER.BETRADAR;

  if (isGhostUser()) {
    toggleGhostUserDialog({ dialogType: GHOST_DIALOG_TYPE.WATCH_STREAM_LOG_IN });
    return;
  }

  if (isBetradar && !hasMadeAFirstDeposit()) {
    toggleGhostUserDialog({ dialogType: GHOST_DIALOG_TYPE.WATCH_STREAM_MAKE_FIRST_DEPOSIT });
    return;
  }

  setStreamIframeEventId(eventId);
  setStreamingSupplier(supplier);

  if (isBetradar) {
    const success = await handleBetradarStreamingSetup(eventId);
    if (!success) {
      return;
    }
  } else {
    setStreamIframeSource(stream);
  }

  posthogTrackStreamOpen({ ...trackData, supplier: supplier || STREAMING_SUPPLIER.UNDEFINED });
};

export const isStreamingEnabledForSport = (sportId: number) => {
  switch (sportId) {
    case sportCategories.HORSES:
      return HORSE_STREAMING_ENABLED;
    case sportCategories.GREY_HOUNDS:
      return GREYHOUND_STREAMING_ENABLED;
    default:
      return BETRADAR_STREAMING_ENABLED;
  }
};

export const isEventInSubscriptionQueue = (eventsToSubscribe: SubscribeEventInfo[], eventInfo: SubscribeEventInfo) => {
  for (const subscribedEvent of eventsToSubscribe) {
    if (eventInfo.id === subscribedEvent.id && eventInfo.sportId === subscribedEvent.sportId) {
      return true;
    }
  }

  return false;
};

export const shouldRemoveOneMarket = (
  numberOfOffers: number,
  selectedEventId: string | null,
  shouldDisplayOne: boolean,
  shouldDisplayTwo: boolean,
  shouldDisplayThree: boolean,
) => {
  if (selectedEventId && numberOfOffers >= 1 && shouldDisplayOne && !shouldDisplayTwo) {
    return true;
  }

  if (selectedEventId && numberOfOffers >= 2 && shouldDisplayTwo && !shouldDisplayThree) {
    return true;
  }

  return !!(selectedEventId && numberOfOffers >= 3 && shouldDisplayThree);
};

export const getMarketsForDropdownButtons = (
  selectedEventId: string | null,
  offers: DropdownMarket[],
  selectedIndexesForColumnDropdowns: DropdownSelectedIndexes,
) => {
  const markets: DropdownMarket[] = [];
  const firstOfferOption = offers[selectedIndexesForColumnDropdowns.firstIndex] || undefined;
  const secondOfferOption = offers[selectedIndexesForColumnDropdowns.secondIndex] || undefined;
  const thirdOfferOption = offers[selectedIndexesForColumnDropdowns.thirdIndex] || undefined;

  const currentWidth = window.innerWidth;
  const shouldBeOneOffer = currentWidth < SCREEN_SIZE_FOR_TWO_OUTCOMES;
  const shouldBeTwoOffers = currentWidth >= SCREEN_SIZE_FOR_TWO_OUTCOMES;
  const shouldBeThreeOffers = currentWidth >= SCREEN_SIZE_FOR_THREE_OUTCOMES;

  if (firstOfferOption) {
    markets.push(firstOfferOption);
  }

  if (secondOfferOption && shouldBeTwoOffers) {
    markets.push(secondOfferOption);
  }

  if (thirdOfferOption && shouldBeThreeOffers) {
    markets.push(thirdOfferOption);
  }

  if (shouldRemoveOneMarket(offers.length, selectedEventId, shouldBeOneOffer, shouldBeTwoOffers, shouldBeThreeOffers)) {
    markets.splice(-1, 1);
  }

  return markets;
};

// `event:list` endpoint returns a small amount of offers with outcomes
// so in order the event to contain the offer & outcome selected from the event card
// we check here if this offer exists or not on the event received in `event:list`
// If it doesn't exist, then we forcefully add the offer with the outcome to the event
export const addOfferWithOutcomeToTheEvent = (offerId: number, outcome: MarketOutcome, event: EventItem, marketOption: DropdownMarket) => {
  let fullDetailedEvent = { ...event };

  if (fullDetailedEvent.details && fullDetailedEvent.details.offers.filter(o => o.id === offerId).length === 0) {
    const offerMandatoryData: Offer = {
      id: offerId,
      name: marketOption.name,
      uniqueCode: String(offerId),
      outcomes: [
        {
          outcomeName: outcome.name.long,
          outcomeId: outcome.id,
          odds: outcome.odds,
          oddsStr: String(outcome.odds.toFixed(2)),
          fractional: outcome.fractionalOdds,
          feedOdds: 0,
          margin: 0,
          quickbet: false,
        },
      ],
      sourceOfferTypeId: marketOption.sourceOfferTypeId,
      sourceOfferTypeInputSourceId: marketOption.sourceOfferTypeInputSourceId,
      status: offerStatuses.ENABLED,
      priority: 1,
    };

    fullDetailedEvent = {
      ...fullDetailedEvent,
      details: {
        ...fullDetailedEvent.details,
        offers: [...fullDetailedEvent.details.offers, offerMandatoryData],
      },
    };
  }

  return fullDetailedEvent;
};

export const findLiveEventFor = (ticket: Ticket, events: SportsbookEventsState) => {
  for (const key of getKeysFromEventsMap(events)) {
    const event = events[key];

    if (event && ticket && ticket.bets.length > 0 && ticket.bets[0].event.id === event.id) {
      return event;
    }
  }

  return undefined;
};

export const findLiveEventsFor = (ticket: Ticket, events: SportsbookEventsState) => {
  const liveEvents: EventItem[] = [];

  for (const key of getKeysFromEventsMap(events)) {
    const event = events[key];

    if (event && ticket.bets.some(bet => bet.event.id === event.id)) {
      liveEvents.push(event);
    }
  }

  return liveEvents.length > 0 ? liveEvents : undefined;
};

export const separateLiveEvents = (eventsList: EventItem[]) => {
  const inPlayEvents: EventItem[] = [];
  const otherEvents: EventItem[] = [];

  eventsList.forEach(event => {
    if (event.status === eventStatuses.LIVE) {
      inPlayEvents.push(event);
    } else {
      otherEvents.push(event);
    }
  });

  return { inPlayEvents, otherEvents };
};

export const getEventDetails = async (sportId: number, eventId: number) => {
  try {
    return await getEvent(sportId, eventId);
  } catch (e) {
    return undefined;
  }
};

export const getRaceDetails = async (eventId: number) => {
  try {
    return await getRace(eventId);
  } catch (e) {
    return undefined;
  }
};

export const shouldScrollTopOfEventsList = (locationData: SportsbookLocation) => {
  const { sportId, sportSection } = locationData;

  if (sportId === SPORT_LIST_ID.IN_PLAY) {
    return false;
  }

  if (sportSection === SPORT_LIST_ID.COMPETITIONS) {
    return false;
  }

  return true;
};
